import { useMemo } from "react";
import { useEmailDesigns } from "../../email-design/hooks/use-email-design";
import { makeEmailToolbarTopLevelElements } from "../utils";

import type { EmailDesign } from "../../email-design/server/supabase-email-design-service";
import type { GenericToolbarProps } from "../../message/editor/plugins/generic-toolbar/generic-toolbar";
import type {
  GenericToolbarDropdownElement,
  GenericToolbarElement,
  NodeTypeToolbarElements,
} from "../../message/editor/plugins/generic-toolbar/types";
import type { EmailDesignTemplateSettings } from "../../message/editor/types/serialised-editor-design";

export type ToolbarSettings = Pick<
  GenericToolbarProps,
  "channel" | "topLevelElements" | "rightAlignedElements"
> & {
  floatingElements?: NodeTypeToolbarElements;
};

interface UseEmailToolbarSettingsParams {
  selectedEmailDesign: EmailDesign | null;
  templateSettings?: EmailDesignTemplateSettings;
  setEmailDesign: (design?: EmailDesign | null) => void;
  blacklistedElements?: (GenericToolbarElement["type"] | GenericToolbarDropdownElement["type"])[];
  isDirty?: boolean;
}

export function useEmailToolbarSettings({
  selectedEmailDesign,
  setEmailDesign,
  blacklistedElements = [],
  isDirty = false,
}: UseEmailToolbarSettingsParams): ToolbarSettings | null {
  const { data: emailDesigns } = useEmailDesigns();

  return useMemo(() => {
    if (!selectedEmailDesign) return null;

    const floatingElements: NodeTypeToolbarElements = {
      text: [{ type: "bold" }, { type: "italic" }, { type: "underline" }, { type: "linkButton" }],
      unsavedImage: [
        { type: "saveImageButton" },
        { type: "deleteNodeButton" },
        { type: "imageFullWidthButton" },
        { type: "alignmentButtons" },
        { type: "linkButton" },
      ],
      savedImage: [
        { type: "deleteNodeButton" },
        { type: "imageFullWidthButton" },
        { type: "alignmentButtons" },
        { type: "linkButton" },
      ],
      button: null,
      unsavedDesignHuddle: [
        { type: "saveImageButton" },
        { type: "deleteNodeButton" },
        { type: "imageFullWidthButton" },
        { type: "alignmentButtons" },
        { type: "linkButton" },
        { type: "editButton" },
      ],
      savedDesignHuddle: [
        { type: "deleteNodeButton" },
        { type: "imageFullWidthButton" },
        { type: "alignmentButtons" },
        { type: "linkButton" },
        { type: "editButton" },
        { type: "videoLinkButton" } as const,
      ],
      poll: [{ type: "editButton" }, { type: "deleteNodeButton" }],
      videoAsset: [
        { type: "deleteNodeButton" },
        { type: "imageFullWidthButton" },
        { type: "alignmentButtons" },
        { type: "videoLinkButton" } as const,
      ],
    };

    const result = {
      channel: "email" as const,
      floatingElements,
      topLevelElements: makeEmailToolbarTopLevelElements({
        settings: {
          emailDesigns: emailDesigns ?? [],
          selectedEmailDesign,
          setEmailDesign,
          isDirty,
        },
      }),
    };

    result.topLevelElements = result.topLevelElements
      .filter((el) => el.type !== "toolbarDropDown" || el.children?.length)
      .map((el) => {
        if (el.type === "toolbarDropDown") {
          return {
            ...el,
            children: el.children.filter((c) => !blacklistedElements.includes(c.type)),
          };
        }
        return el;
      })
      .filter((el) => !blacklistedElements.includes(el.type));

    // Filter floating items as well
    for (const key in result.floatingElements) {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const val = result.floatingElements[key as keyof NodeTypeToolbarElements];
      if (Array.isArray(val)) {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        result.floatingElements[key as keyof NodeTypeToolbarElements] = val.filter(
          (el) => !blacklistedElements.includes(el.type),
        );
      }
    }

    return result;
  }, [selectedEmailDesign, emailDesigns, setEmailDesign, blacklistedElements, isDirty]);
}
